import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Flag, MessageCircle, MoreVertical, Send, ShieldBan, ShieldOff } from 'lucide-react';
import { apiRequest } from '../utils/api';
import { useAuth } from '../components/auth/AuthComponents';
import { isLawyerRole } from '../utils/userPermissions';

const REPORT_REASONS = [
  { value: 'spam', label: 'Spam' },
  { value: 'harassment', label: 'Harassment' },
  { value: 'inappropriate', label: 'Inappropriate content' },
  { value: 'other', label: 'Other' },
];

function formatWhen(value) {
  if (!value) return '';
  const date = new Date(value);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function MessagesPage() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const selectedId = conversationId ? Number(conversationId) : null;
  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('spam');
  const [reportDetails, setReportDetails] = useState('');
  const [reportMessageId, setReportMessageId] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const bottomRef = useRef(null);
  const menuRef = useRef(null);

  const loadList = useCallback(async () => {
    setListLoading(true);
    try {
      const data = await apiRequest('messages/conversations');
      setList(data.conversations || []);
    } catch (e) {
      setError(e.message || 'Failed to load messages');
    } finally {
      setListLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (id) => {
    if (!id) {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    setError('');
    try {
      const data = await apiRequest(`messages/conversations/${id}`);
      setDetail(data);
      await loadList();
    } catch (e) {
      setError(e.message || 'Failed to load conversation');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, [loadList]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
    else setDetail(null);
    setMenuOpen(false);
    setShowBlockConfirm(false);
    setShowReportModal(false);
  }, [selectedId, loadDetail]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [detail?.messages?.length]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!selectedId || !reply.trim() || detail?.can_message === false) return;
    setSending(true);
    setError('');
    try {
      await apiRequest(`messages/conversations/${selectedId}/messages`, {
        method: 'POST',
        body: { body: reply.trim() },
      });
      setReply('');
      await loadDetail(selectedId);
    } catch (err) {
      setError(err.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleBlock = async () => {
    if (!selectedId) return;
    setActionBusy(true);
    setError('');
    try {
      await apiRequest(`messages/conversations/${selectedId}/block`, { method: 'POST' });
      setShowBlockConfirm(false);
      setMenuOpen(false);
      await loadDetail(selectedId);
    } catch (err) {
      setError(err.message || 'Failed to block user');
    } finally {
      setActionBusy(false);
    }
  };

  const handleUnblock = async () => {
    if (!selectedId) return;
    setActionBusy(true);
    setError('');
    try {
      await apiRequest(`messages/conversations/${selectedId}/block`, { method: 'DELETE' });
      setMenuOpen(false);
      await loadDetail(selectedId);
    } catch (err) {
      setError(err.message || 'Failed to unblock user');
    } finally {
      setActionBusy(false);
    }
  };

  const openReport = (messageId = null) => {
    setReportMessageId(messageId);
    setReportReason('spam');
    setReportDetails('');
    setShowReportModal(true);
    setMenuOpen(false);
  };

  const handleReport = async (e) => {
    e.preventDefault();
    if (!selectedId) return;
    if (reportReason === 'other' && !reportDetails.trim()) {
      setError('Please add details for “Other”.');
      return;
    }
    setActionBusy(true);
    setError('');
    try {
      await apiRequest(`messages/conversations/${selectedId}/report`, {
        method: 'POST',
        body: {
          reason: reportReason,
          details: reportDetails.trim() || null,
          message_id: reportMessageId,
        },
      });
      setShowReportModal(false);
      setError('');
      alert('Report submitted. Thank you — our team will review it.');
    } catch (err) {
      setError(err.message || 'Failed to submit report');
    } finally {
      setActionBusy(false);
    }
  };

  const isLawyer = isLawyerRole(user?.role);
  const canReply = Boolean(user) && detail?.can_message !== false;
  const counterpartLabel = detail
    ? detail.my_role === 'lawyer'
      ? detail.client_name
      : detail.lawyer_display_name
    : 'user';

  return (
    <div className="min-h-full text-[var(--color-text)] bg-[var(--color-bg)]">
      <Helmet>
        <title>Messages | DocxsAi</title>
      </Helmet>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 pb-10">
        <div className={`mb-6 ${selectedId ? 'hidden lg:block' : ''}`}>
          <h1 className="text-2xl sm:text-3xl font-bold text-[var(--color-text)] flex items-center gap-2">
            <MessageCircle className="w-7 h-7 text-[var(--color-primary)]" />
            Messages
          </h1>
          <p className="text-[var(--color-text-muted)] text-sm mt-1">
            Chat with {isLawyer ? 'clients who contacted you' : 'lawyers you reached out to'} via Find a Lawyer.
          </p>
        </div>

        {error ? (
          <div className="mb-4 rounded-xl border border-red-500/30 bg-red-900/20 p-3 text-red-200 text-sm">{error}</div>
        ) : null}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 min-h-[520px]">
          <div className={`lg:col-span-2 elevated-card border border-[var(--color-border)] rounded-2xl overflow-hidden flex flex-col ${selectedId ? 'hidden lg:flex' : 'flex'}`}>
            <div className="px-4 py-3 border-b border-[var(--color-border)] text-sm font-semibold text-[var(--color-text-secondary)]">Conversations</div>
            <div className="flex-1 overflow-y-auto">
              {listLoading ? (
                <p className="p-4 text-[var(--color-text-muted)] text-sm">Loading…</p>
              ) : list.length === 0 ? (
                <div className="p-6 text-center text-[var(--color-text-muted)] text-sm">
                  No conversations yet.
                  {!isLawyer ? (
                    <p className="mt-2">
                      <Link to="/find-a-lawyer" className="text-[var(--color-primary)] hover:text-[var(--color-primary)]">Find a Lawyer</Link>
                      {' '}to start one.
                    </p>
                  ) : null}
                </div>
              ) : (
                list.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => navigate(`/messages/${item.id}`)}
                    className={`w-full text-left px-4 py-3 border-b border-[var(--color-border)] hover:elevated-card transition-colors ${
                      selectedId === item.id ? 'bg-[var(--color-primary-soft)]' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-semibold text-[var(--color-text)] truncate">
                        {item.my_role === 'lawyer' ? item.client_name : item.lawyer_display_name}
                      </p>
                      {item.unread ? <span className="shrink-0 w-2 h-2 rounded-full bg-[var(--color-primary)] mt-2" /> : null}
                    </div>
                    <p className="text-xs text-[var(--color-text-muted)] mt-1 truncate">{item.last_message_preview || 'No messages'}</p>
                    <p className="text-[11px] text-gray-600 mt-1">{formatWhen(item.last_message_at)}</p>
                  </button>
                ))
              )}
            </div>
          </div>

          <div className={`lg:col-span-3 elevated-card border border-[var(--color-border)] rounded-2xl flex flex-col min-h-[420px] ${!selectedId ? 'hidden lg:flex' : 'flex'}`}>
            {!selectedId ? (
              <div className="flex-1 flex items-center justify-center text-[var(--color-text-muted)] text-sm p-6 text-center">
                Select a conversation to view messages and reply.
              </div>
            ) : detailLoading && !detail ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : detail ? (
              <>
                <div className="px-4 py-4 border-b border-[var(--color-border)] space-y-2">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => navigate('/messages')}
                      className="lg:hidden p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                      aria-label="Back to conversations"
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </button>
                    <p className="text-lg font-semibold text-[var(--color-text)] flex-1 truncate">
                      {counterpartLabel}
                    </p>
                    {detail.counterpart_user_id ? (
                      <div className="relative" ref={menuRef}>
                        <button
                          type="button"
                          onClick={() => setMenuOpen((v) => !v)}
                          className="p-2 rounded-lg text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-surface-muted)]"
                          aria-label="Conversation actions"
                        >
                          <MoreVertical className="w-5 h-5" />
                        </button>
                        {menuOpen ? (
                          <div className="absolute right-0 mt-1 w-48 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] shadow-xl z-20 py-1">
                            {detail.i_blocked ? (
                              <button
                                type="button"
                                disabled={actionBusy}
                                onClick={handleUnblock}
                                className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-muted)]"
                              >
                                <ShieldOff className="w-4 h-4" />
                                Unblock user
                              </button>
                            ) : (
                              <button
                                type="button"
                                disabled={actionBusy}
                                onClick={() => {
                                  setMenuOpen(false);
                                  setShowBlockConfirm(true);
                                }}
                                className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left text-red-300 hover:bg-[var(--color-surface-muted)]"
                              >
                                <ShieldBan className="w-4 h-4" />
                                Block user
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => openReport(null)}
                              className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-muted)]"
                            >
                              <Flag className="w-4 h-4" />
                              Report user
                            </button>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                  {detail.my_role !== 'lawyer' && detail.lawyer_public_slug ? (
                    <Link
                      to={`/lawyers/${detail.lawyer_public_slug}`}
                      className="text-sm text-[var(--color-primary)] hover:text-[var(--color-primary)]"
                    >
                      View lawyer profile
                    </Link>
                  ) : null}
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {detail.messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm group relative ${
                        msg.is_mine
                          ? 'ml-auto brand-gradient-btn'
                          : 'mr-auto bg-[var(--color-surface-muted)] text-[var(--color-text)] border border-[var(--color-border)]'
                      }`}
                    >
                      {!msg.is_mine ? (
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <p className="text-[11px] font-semibold text-[var(--color-primary)]">
                            {msg.sender_name || (detail.my_role === 'client' ? detail.lawyer_display_name : detail.client_name)}
                          </p>
                          {detail.counterpart_user_id ? (
                            <button
                              type="button"
                              onClick={() => openReport(msg.id)}
                              className="opacity-0 group-hover:opacity-100 text-[10px] text-[var(--color-text-muted)] hover:text-red-300"
                              title="Report this message"
                            >
                              Report
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                      <p className="whitespace-pre-wrap leading-relaxed">{msg.body}</p>
                      <p className={`text-[10px] mt-1.5 ${msg.is_mine ? 'text-[var(--color-primary)]/80' : 'text-[var(--color-text-muted)]'}`}>
                        {formatWhen(msg.created_at)}
                      </p>
                    </div>
                  ))}
                  <div ref={bottomRef} />
                </div>

                {detail.i_blocked ? (
                  <div className="p-4 border-t border-[var(--color-border)] text-sm text-amber-200/90 bg-amber-900/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <span>You blocked this user. They cannot message you, and you cannot send messages.</span>
                    <button
                      type="button"
                      disabled={actionBusy}
                      onClick={handleUnblock}
                      className="shrink-0 px-3 py-1.5 rounded-lg border border-amber-500/40 text-amber-100 hover:bg-amber-500/10"
                    >
                      Unblock
                    </button>
                  </div>
                ) : detail.blocked_by_other ? (
                  <div className="p-4 border-t border-[var(--color-border)] text-sm text-[var(--color-text-muted)] bg-gray-950/40">
                    You are blocked and cannot send messages in this conversation.
                  </div>
                ) : canReply ? (
                  <form onSubmit={handleSend} className="p-4 border-t border-[var(--color-border)] flex gap-2">
                    <input
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      placeholder="Type your reply…"
                      className="flex-1 px-4 py-2.5 rounded-xl bg-[var(--color-input-bg)] border border-[var(--color-border)] text-[var(--color-text)] placeholder-[var(--color-placeholder)] focus:outline-none focus:border-[var(--color-primary)]"
                    />
                    <button
                      type="submit"
                      disabled={sending || !reply.trim()}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl brand-gradient-btn disabled:opacity-50 font-semibold"
                    >
                      <Send className="w-4 h-4" />
                      Send
                    </button>
                  </form>
                ) : (
                  <div className="p-4 border-t border-[var(--color-border)] text-sm text-[var(--color-text-muted)]">
                    <Link to="/login" className="text-[var(--color-primary)] hover:text-[var(--color-primary)]">Sign in</Link>
                    {' '}to reply in this conversation.
                  </div>
                )}
              </>
            ) : null}
          </div>
        </div>
      </div>

      {showBlockConfirm ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
          <div className="w-full max-w-md rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5 shadow-2xl">
            <h2 className="text-lg font-semibold text-[var(--color-text)]">Block {counterpartLabel}?</h2>
            <p className="text-sm text-[var(--color-text-muted)] mt-2">
              You will not be able to send or receive messages with this user until you unblock them.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowBlockConfirm(false)}
                className="px-4 py-2 rounded-xl text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-muted)]"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={actionBusy}
                onClick={handleBlock}
                className="px-4 py-2 rounded-xl bg-red-600 hover:bg-red-500 text-[var(--color-text)] font-semibold disabled:opacity-50"
              >
                Block
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showReportModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
          <form
            onSubmit={handleReport}
            className="w-full max-w-md rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5 shadow-2xl space-y-4"
          >
            <h2 className="text-lg font-semibold text-[var(--color-text)]">
              Report {reportMessageId ? 'message' : counterpartLabel}
            </h2>
            <p className="text-sm text-[var(--color-text-muted)]">
              Tell us why you&apos;re reporting. Our team will review this.
            </p>
            <label className="block text-sm text-[var(--color-text-secondary)]">
              Reason
              <select
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                className="mt-1 w-full px-3 py-2.5 rounded-xl bg-gray-950 border border-[var(--color-border)] text-[var(--color-text)]"
              >
                {REPORT_REASONS.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </label>
            <label className="block text-sm text-[var(--color-text-secondary)]">
              Details {reportReason === 'other' ? '(required)' : '(optional)'}
              <textarea
                value={reportDetails}
                onChange={(e) => setReportDetails(e.target.value)}
                rows={3}
                maxLength={2000}
                className="mt-1 w-full px-3 py-2.5 rounded-xl bg-gray-950 border border-[var(--color-border)] text-[var(--color-text)] placeholder-[var(--color-placeholder)]"
                placeholder="Add any extra context…"
              />
            </label>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowReportModal(false)}
                className="px-4 py-2 rounded-xl text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-muted)]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={actionBusy}
                className="px-4 py-2 rounded-xl brand-gradient-btn font-semibold disabled:opacity-50"
              >
                Submit report
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}
